import type { PropertyDTO } from "@zplanem/responses"
import { webPaths } from "@zplanem/web-paths"
import { useNavigate } from "react-router-dom"
import { cn } from "../../lib/utils"
import { usePropertyMemberships } from "../hooks/use-property-memberships"
import { getTranslations } from "../i18n"
import { useLocale } from "../locale-context"
import { useUser } from "../user-context"
import { ChatRailToggleButton } from "./chat-rail-toggle-button"
import { ManageMenu } from "./manage-menu"
import { ShareButton } from "./share-button"

interface Props {
	property: PropertyDTO
	onShareClick: () => void
	/**
	 * Members-chat rail integration. Optional so pages that have no
	 * shared-chat surface (documents, journal, edit, transfer, …) can mount
	 * the cluster without the toggle button.
	 */
	chatRailOpen?: boolean
	chatUnreadCount?: number
	onChatRailToggle?: () => void
	/** Toggle label override — "Property chat" / "Project chat" per context. */
	chatToggleLabel?: string
	className?: string
}

/**
 * The contextual page-action cluster (ZPL-353): members-chat toggle (with
 * unread count), Share, and the owner/architect-gated Manage menu. Lives at
 * the top-right of every property/project page — inside the in-page header
 * on md+ and inside the mobile-only PropertyTopBar below md. One component
 * so the permission gating is derived in exactly one place.
 */
export function PageActions({
	property,
	onShareClick,
	chatRailOpen,
	chatUnreadCount,
	onChatRailToggle,
	chatToggleLabel,
	className,
}: Props) {
	const navigate = useNavigate()
	const { locale } = useLocale()
	const t = getTranslations(locale).properties.detail
	const { profile } = useUser()
	const memberships = usePropertyMemberships(property.id)

	const isOwner =
		memberships.data?.owner.id !== undefined &&
		memberships.data.owner.id === profile?.id
	const isArchitectMember = Boolean(
		memberships.data?.memberships.some(
			(m) => m.user.id === profile?.id && m.role === "architect",
		),
	)

	const items: Array<{ label: string; onSelect: () => void }> = []
	if (isOwner || isArchitectMember) {
		items.push({
			label: t.manageMenu.edit,
			onSelect: () => navigate(webPaths.propertyEdit(property.id)),
		})
	}
	if (isOwner) {
		items.push({
			label: t.manageMenu.transfer,
			onSelect: () => navigate(webPaths.propertyTransfer(property.id)),
		})
	}

	return (
		<div className={cn("flex items-center gap-2", className)}>
			{onChatRailToggle ? (
				<ChatRailToggleButton
					open={chatRailOpen ?? false}
					unread={chatUnreadCount ?? 0}
					onClick={onChatRailToggle}
					label={chatToggleLabel}
				/>
			) : null}
			<ShareButton onClick={onShareClick} />
			<ManageMenu tooltipLabel={t.manageLabel} items={items} />
		</div>
	)
}
